import { supabase } from "@/lib/supabase";
import { env } from "@/lib/env";

/**
 * Insert a like via the security-definer RPC. The RPC also materialises a
 * mutual match and enqueues the "it's a match" emails for both sides — the
 * frontend no longer needs the peer's email to send those.
 *
 * On a fresh match we additionally fire-and-forget a call to the
 * `notify-meetup-match` Edge Function, which DMs each side via Telegram
 * if they have linked the bot. We don't await it — Telegram failure
 * shouldn't block the user's like animation, and email is the fallback
 * channel anyway.
 */
export async function insertLike(
  eventId: string,
  fromId: string,
  toId: string,
): Promise<{ matched: boolean }> {
  const { data, error } = await supabase.rpc("insert_meetup_like", {
    p_from_participant_id: fromId,
    p_to_participant_id: toId,
  });
  if (error) throw new Error(error.message);
  const matched = Boolean(data);
  if (matched) {
    void fireTelegramMatchNotification(eventId, fromId, toId);
  } else {
    // Single-direction like — TZ block E push #1. Notify the
    // recipient with an intrigue line; the Edge Function dedups via
    // meetup_telegram_pushes so re-likes are no-ops.
    void fireTelegramLikePush(eventId, fromId, toId);
  }
  return { matched };
}

function fireTelegramLikePush(
  eventId: string,
  fromId: string,
  toId: string,
): void {
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/send-meetup-like-push`;
  fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      event_id: eventId,
      from_participant_id: fromId,
      to_participant_id: toId,
    }),
  }).catch(() => {
    /* swallow — push is a nudge, not a guarantee */
  });
}

/**
 * Fire-and-forget call into the notify-meetup-match Edge Function. We
 * deliberately catch all errors and never throw — this is a best-effort
 * additional channel on top of the already-enqueued email.
 */
function fireTelegramMatchNotification(
  eventId: string,
  fromId: string,
  toId: string,
): void {
  const endpoint = `${env.VITE_SUPABASE_URL}/functions/v1/notify-meetup-match`;
  fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({
      event_id: eventId,
      a_participant_id: fromId,
      b_participant_id: toId,
    }),
  }).catch(() => {
    /* swallow — email fallback already in the pipeline */
  });
}

export async function fetchLikedIds(
  _eventId: string,
  participantId: string,
): Promise<Set<string>> {
  const { data, error } = await supabase.rpc("find_my_meetup_likes", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  return new Set(((data ?? []) as { to_participant_id: string }[]).map((r) => r.to_participant_id));
}

export interface MeetupMatchRow {
  match_id: string;
  event_id: string;
  other_id: string;
  other_name: string;
  other_role: string | null;
  other_company: string | null;
  other_video_storage_path: string | null;
  other_profile_json: Record<string, unknown> | null;
  other_email: string | null;
  other_contact_for_matches: string | null;
  revealed: boolean;
  created_at: string;
}

export async function fetchMatches(
  _eventId: string,
  participantId: string,
): Promise<MeetupMatchRow[]> {
  const { data, error } = await supabase.rpc("find_my_meetup_matches", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as MeetupMatchRow[];
}

export async function fetchIncomingLikesCount(
  _eventId: string,
  participantId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc("count_my_meetup_incoming_likes", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  return typeof data === "number" ? data : 0;
}
